import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, ChevronLeft, ChevronRight, Check, X, Clock, 
  Users, CalendarDays, Edit2, Save, Filter,
  UserCheck, UserX, History, Shield, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AttendanceDashboard = ({ user, onLogout }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [stats, setStats] = useState(null);
  const [filterUser, setFilterUser] = useState('all');

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const isAgent = user?.role === 'agent';
  const canEdit = isAdmin || isManager;

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/attendance`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year, month }
      });
      setAttendanceData(response.data);
    } catch (error) {
      toast.error('Failed to fetch attendance data');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/attendance/logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { log_type: 'all', page: 1, page_size: 50 }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch logs');
    }
  };

  const fetchStats = async () => {
    if (!isAdmin) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/attendance/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year, month }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  useEffect(() => {
    fetchAttendance();
    if (isAdmin) {
      fetchStats();
    }
  }, [fetchAttendance, year, month, isAdmin]);

  const handlePrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const handleAttendanceChange = (userEmail, date, field, value) => {
    const key = `${userEmail}_${date}`;
    setPendingChanges(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        user_email: userEmail,
        date: date,
        [field]: value
      }
    }));
  };

  const saveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const records = Object.values(pendingChanges).map(change => ({
        user_email: change.user_email,
        date: change.date,
        status: change.status || 'Present',
        late_time: change.late_time || null
      }));

      if (isAdmin) {
        for (const record of records) {
          await axios.put(`${BACKEND_URL}/api/attendance/admin-edit`, record, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      } else {
        await axios.post(`${BACKEND_URL}/api/attendance/mark`, { records }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success(`Attendance saved for ${records.length} records`);
      setPendingChanges({});
      setEditMode(false);
      fetchAttendance();
      if (isAdmin) fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case 'present': return 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-emerald-500/20';
      case 'absent': return 'bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-red-500/20';
      case 'half-day': return 'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-amber-500/20';
      default: return 'bg-slate-200 text-slate-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'present': return 'P';
      case 'absent': return 'A';
      case 'half-day': return 'H';
      default: return '-';
    }
  };

  const getCellValue = (userEmail, date) => {
    const key = `${userEmail}_${date}`;
    if (pendingChanges[key]) {
      return pendingChanges[key];
    }
    const staffMember = attendanceData?.staff?.find(s => s.email === userEmail);
    return staffMember?.attendance?.[date] || null;
  };

  const myAttendance = attendanceData?.staff?.find(s => s.email === user?.email);
  const filteredStaff = attendanceData?.staff?.filter(s => 
    filterUser === 'all' || s.email === filterUser
  ) || [];

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading attendance...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-6 overflow-auto" data-testid="attendance-dashboard">
        <div className="max-w-full mx-auto space-y-5">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white/80 backdrop-blur-sm p-5 rounded-2xl shadow-sm border border-white/50">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <CalendarDays className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Attendance Dashboard
                </h1>
                <p className="text-slate-500 text-sm">Track and manage team attendance</p>
              </div>
            </div>
            
            {/* Month Navigation */}
            <div className="flex items-center gap-2 bg-white rounded-xl p-1.5 shadow-sm border border-slate-200">
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrevMonth}
                className="text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 h-10 w-10 rounded-lg"
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="text-center min-w-[160px] px-4">
                <p className="text-lg font-bold text-slate-800">{attendanceData?.month_name}</p>
                <p className="text-sm text-slate-500">{year}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNextMonth}
                className="text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 h-10 w-10 rounded-lg"
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Agent's Own Stats */}
          {isAgent && myAttendance && (
            <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-2 text-white/80 text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                MY ATTENDANCE - {attendanceData?.month_name} {year}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-white/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-3xl font-bold text-white">{myAttendance.stats?.present || 0}</p>
                  <p className="text-white/70 text-sm">Present</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-white/20 flex items-center justify-center">
                    <X className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-3xl font-bold text-white">{myAttendance.stats?.absent || 0}</p>
                  <p className="text-white/70 text-sm">Absent</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-white/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-3xl font-bold text-white">{myAttendance.stats?.half_day || 0}</p>
                  <p className="text-white/70 text-sm">Half-day</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-white/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-3xl font-bold text-white">{myAttendance.stats?.late || 0}</p>
                  <p className="text-white/70 text-sm">Late</p>
                </div>
              </div>
            </div>
          )}

          {/* Admin Stats */}
          {isAdmin && stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.staff_count}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Staff</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <UserCheck className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{stats.status_breakdown?.Present || 0}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Present</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <UserX className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{stats.status_breakdown?.Absent || 0}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Absent</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{stats.status_breakdown?.['Half-day'] || 0}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Half-day</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{stats.late_arrivals}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Late</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{stats.admin_modifications}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Edits</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Bar */}
          <div className="bg-white/80 backdrop-blur-sm p-4 rounded-2xl shadow-sm border border-white/50">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Filter className="w-4 h-4 text-slate-400" />
                <Select value={filterUser} onValueChange={setFilterUser}>
                  <SelectTrigger className="w-[200px] bg-white border-slate-200 rounded-xl">
                    <SelectValue placeholder="Filter by user" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {attendanceData?.staff?.map(s => (
                      <SelectItem key={s.email} value={s.email}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <Button
                    variant="outline"
                    onClick={() => { fetchLogs(); setShowLogs(!showLogs); }}
                    className="border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl"
                    data-testid="view-logs-btn"
                  >
                    <History className="w-4 h-4 mr-2" />
                    {showLogs ? 'Hide Logs' : 'View Logs'}
                  </Button>
                )}
                
                {canEdit && (
                  <>
                    {editMode ? (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => { setEditMode(false); setPendingChanges({}); }}
                          className="border-slate-200 text-slate-600 rounded-xl"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={saveChanges}
                          disabled={saving || Object.keys(pendingChanges).length === 0}
                          className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl shadow-lg shadow-indigo-500/20"
                          data-testid="save-attendance-btn"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {saving ? 'Saving...' : `Save (${Object.keys(pendingChanges).length})`}
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => setEditMode(true)}
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl shadow-lg shadow-indigo-500/20"
                        data-testid="edit-attendance-btn"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        {isAdmin ? 'Admin Edit' : 'Mark Attendance'}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Logs Panel */}
          {showLogs && isAdmin && logs && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-0 shadow-sm bg-white/80 backdrop-blur-sm rounded-2xl">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-600" />
                    Manager Activity ({logs.total_manager})
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-h-56 overflow-y-auto">
                  {logs.manager_logs?.length > 0 ? (
                    <div className="space-y-2">
                      {logs.manager_logs.map((log, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-xl">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-800">{log.user_email}</span>
                            <span className="text-xs text-slate-500">{new Date(log.timestamp).toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{log.details}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm text-center py-4">No activity yet</p>
                  )}
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm bg-white/80 backdrop-blur-sm rounded-2xl">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    Admin Changes ({logs.total_admin})
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-h-56 overflow-y-auto">
                  {logs.admin_logs?.length > 0 ? (
                    <div className="space-y-2">
                      {logs.admin_logs.map((log, idx) => (
                        <div key={idx} className="p-3 bg-blue-50 rounded-xl">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-800">{log.user_email}</span>
                            <span className="text-xs text-slate-500">{new Date(log.changed_at).toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-slate-600 mt-1">Date: {log.date}</p>
                          <p className="text-xs mt-1">
                            <span className={`px-2 py-0.5 rounded ${getStatusStyle(log.old_status)}`}>{log.old_status}</span>
                            <span className="mx-2 text-slate-400">→</span>
                            <span className={`px-2 py-0.5 rounded ${getStatusStyle(log.new_status)}`}>{log.new_status}</span>
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm text-center py-4">No changes yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Attendance Table */}
          <Card className="border-0 shadow-sm bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-indigo-600" />
                {attendanceData?.month_name} {year} - Attendance Record
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead>
                    <tr className="bg-slate-50/80">
                      <th className="sticky left-0 z-10 bg-slate-100 px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider border-r border-slate-200 min-w-[180px]">
                        Employee
                      </th>
                      {attendanceData?.calendar?.map(day => (
                        <th
                          key={day.date}
                          className={`px-1 py-2 text-center text-xs font-medium min-w-[48px] ${
                            day.is_weekend ? 'bg-slate-200/80 text-slate-400' : 'text-slate-600'
                          }`}
                        >
                          <div className="flex flex-col items-center">
                            <span className="text-xs">{day.day_short}</span>
                            <span className={`text-sm font-bold ${day.is_weekend ? 'text-slate-400' : 'text-slate-700'}`}>
                              {day.day_num}
                            </span>
                          </div>
                        </th>
                      ))}
                      <th className="sticky right-0 z-10 bg-slate-100 px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider border-l border-slate-200 min-w-[100px]">
                        Summary
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaff.map((staffMember, rowIdx) => (
                      <tr 
                        key={staffMember.email} 
                        className={`border-b border-slate-100 hover:bg-indigo-50/30 transition-colors ${
                          staffMember.email === user?.email ? 'bg-indigo-50/50' : ''
                        }`}
                      >
                        <td className="sticky left-0 z-10 bg-white px-4 py-2.5 border-r border-slate-100">
                          <div>
                            <p className="font-semibold text-slate-800 text-sm">{staffMember.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant="outline" className={`text-xs px-1.5 py-0 ${
                                staffMember.role === 'manager' 
                                  ? 'border-purple-300 text-purple-600 bg-purple-50' 
                                  : 'border-indigo-300 text-indigo-600 bg-indigo-50'
                              }`}>
                                {staffMember.role}
                              </Badge>
                              {staffMember.email === user?.email && (
                                <span className="text-xs text-indigo-600 font-medium">(You)</span>
                              )}
                            </div>
                          </div>
                        </td>
                        {attendanceData?.calendar?.map(day => {
                          const cellData = getCellValue(staffMember.email, day.date);
                          const isModifiedByAdmin = cellData?.modified_by_admin;
                          
                          return (
                            <td key={day.date} className={`px-0.5 py-1.5 text-center ${day.is_weekend ? 'bg-slate-100/80' : ''}`}>
                              {day.is_weekend ? (
                                <span className="text-slate-300 text-xs">-</span>
                              ) : editMode ? (
                                <div className="flex flex-col items-center gap-0.5">
                                  <Select
                                    value={cellData?.status || ''}
                                    onValueChange={(value) => handleAttendanceChange(staffMember.email, day.date, 'status', value)}
                                  >
                                    <SelectTrigger className="h-6 w-11 text-xs bg-white border-slate-200 px-1 rounded-lg">
                                      <SelectValue placeholder="-" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Present">P</SelectItem>
                                      <SelectItem value="Absent">A</SelectItem>
                                      <SelectItem value="Half-day">H</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Input
                                    type="text"
                                    placeholder="Time"
                                    value={cellData?.late_time || ''}
                                    onChange={(e) => handleAttendanceChange(staffMember.email, day.date, 'late_time', e.target.value)}
                                    className="h-5 w-11 text-xs text-center bg-white border-slate-200 px-0.5 rounded-lg"
                                  />
                                </div>
                              ) : (
                                <div className="flex flex-col items-center">
                                  {cellData?.status ? (
                                    <>
                                      <span 
                                        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold shadow-sm ${getStatusStyle(cellData.status)} ${isModifiedByAdmin ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
                                        title={isModifiedByAdmin ? 'Modified by Admin' : ''}
                                      >
                                        {getStatusLabel(cellData.status)}
                                      </span>
                                      {cellData.late_time && (
                                        <span className="text-xs text-amber-600 mt-0.5 font-medium">{cellData.late_time}</span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-slate-300 border border-slate-200 border-dashed text-xs">-</span>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="sticky right-0 z-10 bg-white px-2 py-2.5 border-l border-slate-100">
                          <div className="flex flex-col items-center gap-0.5 text-xs">
                            <div className="flex items-center gap-1.5">
                              <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">
                                <Check className="w-3 h-3" /> {staffMember.stats?.present || 0}
                              </span>
                              <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">
                                <X className="w-3 h-3" /> {staffMember.stats?.absent || 0}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">
                                H{staffMember.stats?.half_day || 0}
                              </span>
                              <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold">
                                L{staffMember.stats?.late || 0}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Legend */}
          <div className="bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-white/50">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold shadow-sm ${getStatusStyle('Present')}`}>P</span>
                <span className="text-slate-600">Present</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold shadow-sm ${getStatusStyle('Absent')}`}>A</span>
                <span className="text-slate-600">Absent</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold shadow-sm ${getStatusStyle('Half-day')}`}>H</span>
                <span className="text-slate-600">Half-day</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-amber-600 text-xs font-medium bg-amber-50 px-2 py-1 rounded">10:30</span>
                <span className="text-slate-600">Late Time</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold shadow-sm ${getStatusStyle('Present')} ring-2 ring-blue-400 ring-offset-1`}>P</span>
                <span className="text-slate-600">Admin Modified</span>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default AttendanceDashboard;
